import http from '../../utils/http';

/**
 * Backend/API Gateway may return this when the Cognito JWT is expired even if
 * the client-side expiresIn window in http.js has not yet fired.
 */
export function isExpiredTokenError(errMsg) {
  const msg = `${errMsg || ''}`.toLowerCase();
  return msg.includes('incoming token has expired') || msg.includes('token has expired');
}

export function isSessionExpiredError(err) {
  if (!err) return false;
  const message = err.message || err;
  return message === 'Session Expired' || isExpiredTokenError(message);
}

function defaultCreateError(msg, errData) {
  const error = new Error(msg || 'Request failed');
  if (errData?.errorCode) error.errorCode = errData.errorCode;
  if (errData) error.errorData = errData;
  return error;
}

function expireSession(onSessionExpired, createError) {
  if (onSessionExpired) onSessionExpired();
  // Normalize so callers can treat backend "incoming token has expired" the
  // same as client-side Session Expired (redirect, no inline error text).
  return createError('Session Expired');
}

/**
 * Promise wrapper around http.get with token refresh + session expiry handling.
 * Retries once when the API returns "The incoming token has expired".
 * "Refresh Token Needed" (client-side window) always refreshes without consuming
 * the expired-token retry budget — same as the pre-shared booking API wrappers.
 */
export function httpGetAsPromise(
  url,
  payload,
  user,
  {
    onTokenRefreshed,
    onSessionExpired,
    showLoader = () => {},
    createError = defaultCreateError,
    _retriedAfterRefresh = false,
  } = {},
) {
  return new Promise((resolve, reject) => {
    http.get(
      url,
      payload || {},
      { authorization: `${user.tokenType} ${user.idToken}` },
      showLoader,
      user,
      (data, errMsg, errData) => {
        const retryWithUser = (updatedUser, retriedAfterRefresh) => {
          httpGetAsPromise(url, payload, updatedUser, {
            onTokenRefreshed,
            onSessionExpired,
            showLoader,
            createError,
            _retriedAfterRefresh: retriedAfterRefresh,
          })
            .then(resolve)
            .catch(reject);
        };

        const refreshThenRetry = (retriedAfterRefresh) => {
          http.refreshToken(
            global.URL_TokenRefresh,
            user,
            showLoader,
            (tokenResponse) => {
              const updatedUser = global.updateUserObject(
                user,
                tokenResponse.AuthenticationResult,
              );
              if (onTokenRefreshed) onTokenRefreshed(updatedUser);
              retryWithUser(updatedUser, retriedAfterRefresh);
            },
          );
        };

        if (data === 'Refresh Token Needed') {
          // Proactive client-side refresh; do not mark as expired-token retry.
          refreshThenRetry(_retriedAfterRefresh);
        } else if (data === 'Session Expired') {
          reject(expireSession(onSessionExpired, createError));
        } else if (data === null && isExpiredTokenError(errMsg)) {
          if (_retriedAfterRefresh) {
            reject(expireSession(onSessionExpired, createError));
            return;
          }
          refreshThenRetry(true);
        } else if (data === null) {
          reject(createError(errMsg || 'Request failed', errData));
        } else {
          resolve(data);
        }
      },
    );
  });
}

/**
 * Promise wrapper around http.post with the same auth handling as httpGetAsPromise.
 * @param {string} url
 * @param {object|null|undefined} body
 * @param {object} user
 * @param {{
 *   onTokenRefreshed?: (user: object) => void,
 *   onSessionExpired?: () => void,
 *   showLoader?: (show?: boolean) => void,
 *   createError?: (msg: any, errData: any) => Error,
 *   _retriedAfterRefresh?: boolean,
 * }} [options]
 */
export function httpPostAsPromise(
  url,
  body,
  user,
  {
    onTokenRefreshed,
    onSessionExpired,
    showLoader = () => {},
    createError = defaultCreateError,
    _retriedAfterRefresh = false,
  } = {},
) {
  return new Promise((resolve, reject) => {
    http.post(
      url,
      body || {},
      { headers: { authorization: `${user.tokenType} ${user.idToken}` } },
      showLoader,
      user,
      (data, errMsg, errData) => {
        const retryWithUser = (updatedUser, retriedAfterRefresh) => {
          httpPostAsPromise(url, body, updatedUser, {
            onTokenRefreshed,
            onSessionExpired,
            showLoader,
            createError,
            _retriedAfterRefresh: retriedAfterRefresh,
          })
            .then(resolve)
            .catch(reject);
        };

        const refreshThenRetry = (retriedAfterRefresh) => {
          http.refreshToken(
            global.URL_TokenRefresh,
            user,
            showLoader,
            (tokenResponse) => {
              const updatedUser = global.updateUserObject(
                user,
                tokenResponse.AuthenticationResult,
              );
              if (onTokenRefreshed) onTokenRefreshed(updatedUser);
              retryWithUser(updatedUser, retriedAfterRefresh);
            },
          );
        };

        if (data === 'Refresh Token Needed') {
          refreshThenRetry(_retriedAfterRefresh);
        } else if (data === 'Session Expired') {
          reject(expireSession(onSessionExpired, createError));
        } else if (data === null && isExpiredTokenError(errMsg)) {
          if (_retriedAfterRefresh) {
            reject(expireSession(onSessionExpired, createError));
            return;
          }
          refreshThenRetry(true);
        } else if (data === null) {
          reject(createError(errMsg || 'Request failed', errData));
        } else {
          resolve(data);
        }
      },
    );
  });
}
